import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PasswordIcon from "@mui/icons-material/Password";
import api from "lib/api/api";
import AdminNavbar from "./AdminNavbar";
import "./AdminDashboardLayout.css";

const xmlEscape = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const TeacherCredentialExportPage = () => {
  const [teachers, setTeachers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    api.get("/admin/teacherDetails")
      .then((response) => mounted && setTeachers(Array.isArray(response.data) ? response.data : []))
      .catch((requestError) => mounted && setError(requestError.response?.data?.message || "Failed to load teachers"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const filteredTeachers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return teachers;
    return teachers.filter((teacher) =>
      [teacher.firstName, teacher.lastName, teacher.email, teacher.phoneNumber]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [search, teachers]);

  const selectedTeachers = useMemo(() => {
    const selected = new Set(selectedIds);
    return teachers.filter((teacher) => selected.has(teacher.teacherId));
  }, [selectedIds, teachers]);

  const toggleTeacher = (teacherId) => {
    if (credentials.length) return;
    setSelectedIds((current) =>
      current.includes(teacherId)
        ? current.filter((id) => id !== teacherId)
        : [...current, teacherId]
    );
  };

  const allFilteredSelected = filteredTeachers.length > 0
    && filteredTeachers.every((teacher) => selectedIds.includes(teacher.teacherId));

  const toggleAllFiltered = () => {
    if (credentials.length) return;
    const filteredIds = filteredTeachers.map((teacher) => teacher.teacherId);
    setSelectedIds((current) => {
      if (allFilteredSelected) return current.filter((id) => !filteredIds.includes(id));
      return [...new Set([...current, ...filteredIds])];
    });
  };

  const generatePasswords = async () => {
    setConfirmOpen(false);
    setGenerating(true);
    setError("");
    try {
      const response = await api.post("/admin/teachers/temporary-passwords", {
        teacherIds: selectedIds,
      });
      setCredentials(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Failed to generate temporary passwords");
    } finally {
      setGenerating(false);
    }
  };

  const exportExcel = () => {
    if (!credentials.length) return;
    const headers = ["Teacher ID", "Business Key", "First Name", "Last Name", "Email", "Phone", "Temporary Password"];
    const rows = credentials.map((item) => [
      item.teacherId,
      item.businessKey,
      item.firstName,
      item.lastName,
      item.email,
      item.phoneNumber,
      item.temporaryPassword,
    ]);
    const cell = (value) => `<Cell><Data ss:Type="String">${xmlEscape(value)}</Data></Cell>`;
    const sheetRows = [headers, ...rows]
      .map((row) => `<Row>${row.map(cell).join("")}</Row>`)
      .join("");
    const workbook = `<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?>\n`
      + `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">`
      + `<Worksheet ss:Name="Teacher credentials"><Table>${sheetRows}</Table></Worksheet></Workbook>`;
    const blob = new Blob([workbook], { type: "application/vnd.ms-excel;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `teacher-temporary-credentials-${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const clearSensitiveData = () => {
    setCredentials([]);
    setSelectedIds([]);
  };

  return (
    <div className="admin-dashboard-layout">
      <AdminNavbar />
      <Container className="admin-dashboard-content" maxWidth="xl">
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Teacher Credential Export
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Select teachers, preview their contact details, then generate temporary passwords for a one-time preview and Excel export.
          </Typography>

          <Alert severity="warning" sx={{ mb: 2 }}>
            Generating a temporary password immediately replaces each selected teacher&apos;s current password and invalidates existing sessions. The generated passwords are shown only on this page and are not stored as readable text.
          </Alert>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!credentials.length && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Search by name, email or phone"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ mb: 1 }}
              />
              <FormControlLabel
                control={<Checkbox checked={allFilteredSelected} onChange={toggleAllFiltered} />}
                label={`Select all visible teachers (${filteredTeachers.length})`}
              />
            </Paper>
          )}

          {loading ? <CircularProgress /> : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    {!credentials.length && <TableCell padding="checkbox" />}
                    <TableCell sx={{ fontWeight: 800 }}>Teacher</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Account status</TableCell>
                    {credentials.length > 0 && <TableCell sx={{ fontWeight: 800 }}>Temporary password</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(credentials.length ? credentials : filteredTeachers).map((row) => (
                    <TableRow key={row.teacherId} selected={!credentials.length && selectedIds.includes(row.teacherId)}>
                      {!credentials.length && (
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedIds.includes(row.teacherId)} onChange={() => toggleTeacher(row.teacherId)} />
                        </TableCell>
                      )}
                      <TableCell>{`${row.firstName || ""} ${row.lastName || ""}`.trim() || "—"}</TableCell>
                      <TableCell>{row.email || "—"}</TableCell>
                      <TableCell>{row.phoneNumber || "—"}</TableCell>
                      <TableCell>{credentials.length ? "Password reset" : (row.status || (row.confirmedFromAdmin ? "approved" : "pending"))}</TableCell>
                      {credentials.length > 0 && (
                        <TableCell sx={{ fontFamily: "monospace", fontWeight: 700 }}>{row.temporaryPassword}</TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
            {!credentials.length ? (
              <Button
                variant="contained"
                startIcon={<PasswordIcon />}
                disabled={!selectedTeachers.length || generating}
                onClick={() => setConfirmOpen(true)}
                sx={{ bgcolor: "#FFD600", color: "#111", fontWeight: 800, "&:hover": { bgcolor: "#e6bd00" } }}
              >
                {generating ? "Generating…" : `Generate temporary passwords (${selectedTeachers.length})`}
              </Button>
            ) : (
              <>
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportExcel}>
                  Export Excel
                </Button>
                <Button variant="outlined" color="error" onClick={clearSensitiveData}>
                  Clear preview
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Container>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Reset selected teacher passwords?</DialogTitle>
        <DialogContent>
          This will immediately replace the current passwords for {selectedTeachers.length} teacher(s). They must use the generated temporary passwords until they change them from their profiles.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={generatePasswords}>Generate and reset</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TeacherCredentialExportPage;
